import AppointmentCategoryModal from 'components/AppointmentCategoryModal'
import Button from 'components/Button/Button'
import CheckBox from 'components/CheckBox/CheckBox'
import CourtImage from 'components/CourtImage'
import { Input } from 'components/Input'
import ResetPassword from 'components/ResetPassword'
import Select from 'components/Select'
import { SelectTypes } from 'components/Select/Select.props'
import TextArea from 'components/TextArea/TextArea'
import { find } from 'lodash'
import {
  MyFacilityContainer,
  MyFacilityRow,
} from 'pages/MyFacility/MyFacility.styled'
import React, { useEffect, useState } from 'react'
import { Col } from 'react-grid-system'
import { useParams } from 'react-router'
import { UpdateLocation } from 'services/appointment'
import { useAppDispatch, useAppState } from 'store'
import { appointmentActions } from 'store/slices/appointments/appointments.slice'
import FacilityFormTemplate from 'templates/FacilityFormTemplate'
import {
  MANUAL_PAYMENT_PROCESSOR,
  MEMBER_TYPE,
  ONLINE_PAYMENT_PROCESSOR,
} from 'utils/option-list'
import { useAmplitude } from 'hooks/useAmplitude'
import { AMPLITUDE_EVENTS } from 'amplitude/events'

const FacilityOperationsPage: React.FC = () => {
  const dispatch = useAppDispatch()
  const { locationId } = useParams<{ locationId: string }>()
  const { location, loading } = useAppState(state => state.appointments)
  const [name, setName] = useState<string>('')
  const [address, setAddress] = useState<string>('')
  const [phone, setPhone] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [website, setWebsite] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [receipt_footer, setReceiptFooter] = useState<string>('')
  const [courts, setCourts] = useState<number>(1)
  const [minPrice, setMinPrice] = useState<number>(0)
  const [maxPrice, setMaxPrice] = useState<number>(0)
  const [creditCardFee, setCreditCardFee] = useState<number>(0)
  const [showPswModal, setShowPswModal] = useState<boolean>(false)
  const [showAppointmentModal, setShowAppointmentModal] = useState<boolean>(
    false
  )
  const [memberType, setMemberType] = useState<SelectTypes>()
  const [
    paymentProcessorManual,
    setPaymentProcessorManual,
  ] = useState<SelectTypes>()
  const [
    paymentProcessorInbound,
    setPaymentProcessorInbound,
  ] = useState<SelectTypes>()
  const [supportMin, setSupportMin] = useState<boolean>(false)
  const [reports, setReports] = useState<boolean>(false)
  const [creditSystem, setCreditSystem] = useState<boolean>(false)
  const [timeRestriction, setTimeRestriction] = useState<number>()
  const { logEvent } = useAmplitude()

  useEffect(() => {
    dispatch(appointmentActions.getLocation(locationId))
    dispatch(appointmentActions.getAppointmentColors(locationId))
    logEvent(AMPLITUDE_EVENTS.OPERATIONS_PAGE_EVENT, { location: locationId })
  }, [])

  useEffect(() => {
    if (location) {
      const {
        name,
        address,
        phone_number,
        email,
        website_url,
        courts,
        min_price,
        max_price,
        credit_card_processing_fee,
        description,
        receipt_footer,
        support_30_min,
        support_reports,
        has_credit_system,
        payment_processor_manual,
        payment_processor_inbound,
        booking_time_restriction,
        members,
      } = location
      setTimeRestriction(booking_time_restriction)
      setName(name)
      setAddress(address)
      setPhone(phone_number)
      setEmail(email)
      setWebsite(website_url)
      setDescription(description)
      setReceiptFooter(receipt_footer)
      setCourts(courts.length)
      setMinPrice(min_price)
      setMaxPrice(max_price)
      setCreditCardFee(credit_card_processing_fee)
      setMemberType(find(MEMBER_TYPE, { value: members }))
      setPaymentProcessorManual(
        find(MANUAL_PAYMENT_PROCESSOR, { value: payment_processor_manual })
      )
      setPaymentProcessorInbound(
        find(ONLINE_PAYMENT_PROCESSOR, { value: payment_processor_inbound })
      )
      setSupportMin(support_30_min)
      setReports(support_reports)
      setCreditSystem(has_credit_system)
    }
  }, [location])

  const handleSubmit = async () => {
    if (name && address) {
      const fields: UpdateLocation = {
        locationId,
        body: {
          name,
          address,
        },
      }
      if (email) fields.body.email = email
      if (phone) fields.body.phone = phone
      if (website) fields.body.website = website
      if (minPrice) fields.body.min_price = minPrice
      if (timeRestriction)
        fields.body.booking_time_restriction = timeRestriction
      if (creditCardFee) fields.body.credit_card_processing_fee = creditCardFee
      if (memberType) fields.body.members = memberType.value
      if (description) fields.body.description = description
      if (receipt_footer) fields.body.receipt_footer = receipt_footer
      if (paymentProcessorManual)
        fields.body.payment_processor_manual = paymentProcessorManual.value
      if (paymentProcessorInbound)
        fields.body.payment_processor_inbound = paymentProcessorInbound.value
      fields.body.support_30_min = supportMin
      fields.body.support_reports = reports
      fields.body.support_roaming_pro = creditSystem
      await dispatch(appointmentActions.updateLocation(fields))
    }
  }

  return (
    <FacilityFormTemplate>
      <MyFacilityContainer>
        <MyFacilityRow>
          <Col xs={12} sm={12} md={12} lg={12}>
            <CourtImage uri={location ? location.logo_url : null} />
          </Col>
        </MyFacilityRow>
        <MyFacilityRow>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Input
              fullWidth
              label={'Name'}
              value={name}
              onChange={({ target }) => setName(target.value)}
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Input
              fullWidth
              label={'Address'}
              value={address}
              onChange={({ target }) => setAddress(target.value)}
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Input
              label={'Phone'}
              value={phone}
              format="(###) ###-####"
              mask="_"
              onTextChange={setPhone}
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Input
              fullWidth
              label={'Email'}
              value={email}
              onChange={({ target }) => setEmail(target.value)}
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Input
              fullWidth
              label={'Website'}
              value={website}
              onTextChange={setWebsite}
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Input
              fullWidth
              label={'Number of courts'}
              type="number"
              value={courts}
              onTextChange={court => setCourts(parseInt(court))}
              disabled
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Input
              fullWidth
              label={'Min Price'}
              currencyMask
              value={minPrice.toFixed(2)}
              onChange={({ target }) => {
                setMinPrice(parseFloat(target.value))
              }}
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Input
              fullWidth
              label={'Max Price'}
              currencyMask
              value={maxPrice.toFixed(2)}
              onChange={({ target }) => {
                setMaxPrice(parseFloat(target.value))
              }}
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Input
              fullWidth
              label={'Advance booking restriction (days)'}
              type="number"
              value={timeRestriction}
              onTextChange={time => setTimeRestriction(parseInt(time))}
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Input
              fullWidth
              label={'Credit card processing fee'}
              type="number"
              value={creditCardFee}
              onTextChange={fee => setCreditCardFee(parseFloat(fee))}
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Select
              options={MEMBER_TYPE}
              label="Member type"
              autocomplete
              value={memberType}
              onChange={member => setMemberType(member as SelectTypes)}
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Select
              options={MANUAL_PAYMENT_PROCESSOR}
              label="Manual payment processor"
              autocomplete
              value={paymentProcessorManual}
              onChange={manual =>
                setPaymentProcessorManual(manual as SelectTypes)
              }
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <Select
              options={ONLINE_PAYMENT_PROCESSOR}
              label="Online payment processor"
              autocomplete
              value={paymentProcessorInbound}
              onChange={inbound =>
                setPaymentProcessorInbound(inbound as SelectTypes)
              }
            />
          </Col>
        </MyFacilityRow>
        <MyFacilityRow>
          <Col xs={12} sm={12} md={6} lg={6}>
            <CheckBox
              weight="500"
              text="Support credit system"
              checked={creditSystem}
              onChange={() => setCreditSystem(!creditSystem)}
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <CheckBox
              weight="500"
              text="Support 30 min"
              checked={supportMin}
              onChange={() => setSupportMin(!supportMin)}
            />
          </Col>
          <Col xs={12} sm={12} md={6} lg={6}>
            <CheckBox
              weight="500"
              text="Enable reports"
              checked={reports}
              onChange={() => setReports(!reports)}
            />
          </Col>
          <Col xs={12} sm={12} md={12} lg={12}>
            <div style={{ marginTop: 20 }}>
              <TextArea
                label={'Description'}
                size={96}
                required={false}
                value={description}
                onTextChange={setDescription}
              />
            </div>
          </Col>
          <Col xs={12} sm={12} md={12} lg={12}>
            <div style={{ marginTop: 20 }}>
              <TextArea
                label={'Receipt Footer'}
                size={96}
                required={false}
                value={receipt_footer}
                onTextChange={setReceiptFooter}
              />
            </div>
          </Col>
        </MyFacilityRow>
        <MyFacilityRow>
          <Col
            xs={12}
            sm={12}
            md={4}
            lg={4}
            style={{ justifyContent: 'center', display: 'flex' }}>
            <Button
              text="Appointment Categories"
              variant="primary"
              preset="ghost"
              size={'medium'}
              onClick={() => setShowAppointmentModal(true)}
            />
          </Col>
          <Col
            xs={12}
            sm={12}
            md={4}
            lg={4}
            style={{ justifyContent: 'center', display: 'flex' }}>
            <Button
              text="Change password"
              variant="primary"
              preset="ghost"
              size={'medium'}
              onClick={() => setShowPswModal(true)}
            />
          </Col>
          <Col
            xs={12}
            sm={12}
            md={4}
            lg={4}
            style={{ justifyContent: 'center', display: 'flex' }}>
            <Button
              text="Submit"
              variant="primary"
              preset="filled"
              size={'medium'}
              disabled={loading === 'loading'}
              onClick={handleSubmit}
            />
          </Col>
        </MyFacilityRow>
      </MyFacilityContainer>
      {showPswModal && (
        <ResetPassword onClose={() => setShowPswModal(!showPswModal)} />
      )}
      {showAppointmentModal && (
        <AppointmentCategoryModal
          onClose={() => setShowAppointmentModal(!showAppointmentModal)}
        />
      )}
    </FacilityFormTemplate>
  )
}

export default FacilityOperationsPage
